import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [usuario, setUsuario] = useState(null)
  const [ligaAtual, setLigaAtual] = useState(null)
  const [membroAtual, setMembroAtual] = useState(null)
  const [minhasLigas, setMinhasLigas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) carregarUsuario(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) carregarUsuario(session.user.id)
      else { setUsuario(null); setLigaAtual(null); setMembroAtual(null); setMinhasLigas([]); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function carregarUsuario(userId) {
    const { data: usr } = await supabase.from('usuarios').select('*').eq('id', userId).maybeSingle()
    setUsuario(usr)

    // Busca membros em duas queries separadas para evitar 500 no join RLS
    const { data: membros } = await supabase
      .from('membros_liga')
      .select('id, liga_id, usuario_id, is_admin, vidas_iniciais, vidas_restantes, aprovado, ativo, pagamento_confirmado')
      .eq('usuario_id', userId).eq('aprovado', true).eq('ativo', true)

    const ligaIds = (membros || []).map(m => m.liga_id)
    let ligasData = []
    if (ligaIds.length > 0) {
      const { data: ld } = await supabase
        .from('ligas').select('*').in('id', ligaIds)
      ligasData = ld || []
    }

    const ligas = (membros || []).map(m => {
      const liga = ligasData.find(l => l.id === m.liga_id) || {}
      return { ...liga, membro: m }
    })
    setMinhasLigas(ligas)

    if (ligas.length === 1) {
      _selecionarLiga(ligas[0], membros[0])
    } else if (ligas.length > 0) {
      const ligaSalva = localStorage.getItem('ligaAtualId')
      const ligaEncontrada = ligas.find(l => l.id === parseInt(ligaSalva))
      if (ligaEncontrada) {
        const membro = membros.find(m => m.liga_id === ligaEncontrada.id)
        _selecionarLiga(ligaEncontrada, membro)
      }
    }
    setLoading(false)
  }

  function _selecionarLiga(liga, membro) {
    setLigaAtual(liga)
    setMembroAtual(membro)
    if (liga?.id) localStorage.setItem('ligaAtualId', String(liga.id))
  }

  async function trocarLiga(ligaId) {
    const liga = minhasLigas.find(l => l.id === ligaId)
    if (!liga) return
    const { data: membro } = await supabase
      .from('membros_liga')
      .select('id, liga_id, usuario_id, is_admin, vidas_iniciais, vidas_restantes, aprovado, ativo, pagamento_confirmado')
      .eq('liga_id', ligaId).eq('usuario_id', user.id)
      .maybeSingle()
    _selecionarLiga(liga, membro)
  }

  async function signUp({ primeiroNome, ultimoNome, email, senha, whatsapp, timeFavorito }) {
    const { data, error } = await supabase.auth.signUp({
      email, password: senha,
      options: { data: { primeiro_nome: primeiroNome, ultimo_nome: ultimoNome, whatsapp } }
    })
    if (!error && timeFavorito && data?.user?.id) {
      await supabase.from('usuarios').update({ time_favorito: timeFavorito }).eq('id', data.user.id)
    }
    return { data, error }
  }

  async function signIn({ email, senha }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: senha })
    return { data, error }
  }

  async function signOut() {
    localStorage.removeItem('ligaAtualId')
    await supabase.auth.signOut()
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/nova-senha`
    })
    return { error }
  }

  return (
    <AuthContext.Provider value={{
      user, usuario, ligaAtual, membroAtual, minhasLigas,
      loading, signUp, signIn, signOut, resetPassword,
      trocarLiga, carregarUsuario, selecionarLiga: _selecionarLiga
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
